<template>
  <div class="header-link">
    <a v-if="isUrlLink(link)" :href="link.url" target="_blank" tabindex="-1">
      <button class="header-link-text">{{ link.name }}</button>
    </a>

    <router-link
      v-else-if="isRouteLink(link)"
      :to="getRouteForLink(link)"
      :class="{ 'router-link-active router-link-exact-active': isHeaderGroupActive(link) }"
      tabindex="-1"
      @click="link.action">
      <button class="header-link-text">{{ link.name }}</button>
    </router-link>

    <div v-else-if="isEmpty((link as HeaderRootLink).links)" class="header-link-label whitespace-nowrap">
      {{ link.name }}
    </div>

    <Popover v-else as="div">
      <div class="nav-dropdown-container">
        <PopoverButton>
          <div class="nav-dropdown-button header-link-text">
            <div class="header-link-limited-width">
              {{ (link as HeaderRootLink).name }}
            </div>
            <div class="header-dropdown-icon">
              <MDropDownIcon />
            </div>
          </div>
        </PopoverButton>

        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="scale-95 transform opacity-0"
          enter-to-class="scale-100 transform opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="scale-100 transform opacity-100"
          leave-to-class="scale-95 transform opacity-0">
          <PopoverPanel
            v-slot="{ close }"
            class="header--popover-panel absolute right-0 z-50 origin-top-right divide-y divide-white overflow-hidden rounded border border-white bg-chrome shadow-xl">
            <div v-for="subLink in (link as HeaderRootLink).links" :key="subLink.name">
              <div @click="close">
                <a v-if="isUrlLink(subLink)" :href="(subLink as HeaderUrlLink).url" target="_blank">
                  <div class="header-dropdown-link">{{ subLink.name }}</div>
                </a>
                <router-link v-else :to="getRouteForLink(subLink as HeaderRouteLink)" @click="subLink.action">
                  <div class="header-dropdown-link">{{ subLink.name }}</div>
                </router-link>
              </div>
            </div>
          </PopoverPanel>
        </transition>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import {
  getRouteForLink,
  type HeaderRootLink,
  type HeaderRouteLink,
  type HeaderUrlLink,
  isRouteLink,
  isUrlLink,
} from "@/types/headerLinks";
import { MDropDownIcon } from "@moroku/money-webapp-components";
import { isEmpty } from "radash";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { useRoute } from "vue-router";

defineProps<{
  link: HeaderRootLink;
}>();

const route = useRoute();

// Mark the route as active when it's alias matches as the built-in matching ignores these
const isHeaderGroupActive = (link: HeaderRouteLink) => route.meta.alias === link.routeName;
</script>

<style scoped lang="scss">
.header-link {
  @apply whitespace-nowrap rounded border border-transparent font-heading text-lg transition-colors hover:cursor-pointer;

  a {
    @apply relative;
    color: var(--text-color);
  }

  .router-link-active {
    &:after {
      @apply absolute left-0 top-[3rem] block h-[0.375rem] w-full bg-chromeSecondary;

      content: "";
      transition: opacity ease 0.3s;
    }
    color: var(--active-color);
  }
}

.header--popover-panel {
  .router-link-active {
    color: var(--active-color);
    background-color: var(--active-bg);

    &:after {
      @apply hidden;
    }
  }
}

.header-link-text {
  @apply rounded-full px-4 py-1 font-semibold;

  &:hover {
    color: var(--hover-color);
  }
}

.header-link-label {
  @apply cursor-default rounded-full px-4 py-1 font-semibold;
}

.nav-dropdown-container {
  @apply relative inline-block lg:block;
  color: var(--text-color);

  .header-dropdown-link {
    @apply px-5 py-3 sm:whitespace-nowrap;
    color: var(--text-color);

    &:hover {
      color: var(--hover-color);
      background-color: var(--hover-bg);
    }
  }
}

.header-link-limited-width {
  @apply max-w-48 overflow-hidden text-ellipsis;
}

.nav-dropdown-button {
  @apply flex items-center justify-center font-bold;

  .header-dropdown-icon {
    @apply ml-1.5 w-3.5 pt-0.5;
  }

  &:hover {
    color: var(--hover-color);
    background-color: var(--hover-bg);

    .header-dropdown-icon {
      @apply fill-primary;
    }
  }
}

.header--popover-panel {
  @apply absolute right-0 z-50 origin-top-right divide-y divide-white overflow-hidden rounded border border-white shadow-xl;
  background-color: var(--background-color);
}
</style>
